import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/api';
import { Lock, Zap, RefreshCw } from 'lucide-react';

interface Props {
  isPaymentProcessing?: boolean;
}

export const HardPaywall: React.FC<Props> = ({ isPaymentProcessing }) => {
  const { refreshUser, logout } = useAuth();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    setError(null);
    try {
      const { url } = await apiClient.createCheckoutSession();
      window.location.href = url;
    } catch {
      setError('Failed to start checkout. Please try again.');
      setIsUpgrading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshUser();
    setIsRefreshing(false);
  };

  if (isPaymentProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Zap className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Payment received!</h1>
          <p className="text-gray-600">
            Your account is being activated. This usually takes a few seconds.
          </p>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Checking…' : 'Activate my account'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Lock className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Your trial has expired</h1>
          <p className="text-gray-600">
            Upgrade to Premium to continue using VocaBranch and keep access to all your vocabulary.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
          <ul className="space-y-2 text-sm text-gray-700">
            {['Unlimited vocabulary cards', 'AI-powered enrichment', 'Spaced repetition practice', 'All collections & workspaces'].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            onClick={handleUpgrade}
            disabled={isUpgrading}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <Zap className="h-4 w-4" />
            {isUpgrading ? 'Redirecting to checkout…' : 'Upgrade to Premium'}
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={() => logout()}
            className="text-sm text-gray-400 hover:text-gray-600 underline"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};
